import { atom, useAtomValue, useSetAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { useCallback, useEffect, useMemo } from "react";

/**
 * The one task whose row should glow, because the user just arrived on it from
 * a chip rather than merely having its id to hand.
 *
 * In memory, and cleared once the glow has run, which is the whole point: both
 * clients keep naming the task long after the glow is over — web in the URL,
 * native in the screen's `focus` param, which a tab screen holds for as long as
 * the user stays. A glow keyed on either fires again on every refresh and every
 * return from another filter. The glow answers "this is where you just landed",
 * and that is only true once.
 *
 * One id rather than a set, because only one row can be where the user just
 * landed. A second chip pressed while the first row is still lit supersedes it
 * instead of joining it: two rows glowing at once point in two directions and
 * so point at nothing.
 *
 * Flagged rather than passed as a prop because the row usually is not mounted
 * yet: the chip navigates, the list fetches, and the row arrives a moment
 * later. The flag waits for it, with `FLASH_SAFETY_MS` as a backstop for a row
 * that never comes — a filter that excludes it, a fetch that fails — so a stale
 * flag cannot fire on some unrelated later visit.
 *
 * Shared because the rule is the same on both clients and only the drawing
 * differs: web runs a CSS keyframe, native animates an iOS shadow. Kept apart
 * from `threads/threadFlash.ts`, which does this for thread rows — task ids are
 * strings and thread ids are numbers, and the two surfaces have no reason to
 * share one.
 */
export type TaskFlashState = {
  /** The task a chip asked for. */
  taskId: string;
  /**
   * Which press asked for it. The same chip pressed twice names the same task,
   * so the id alone cannot tell one ask from the next: writing it again is not
   * a change, nothing downstream re-runs, and a user who scrolled away since
   * the first press is returned to where they left off rather than to the row.
   * The row's glow is not over by then either — leaving the tab mid-glow stops
   * the fade without finishing it, so nothing clears the flag until the
   * backstop does.
   */
  press: number;
};

export const flashTaskIdAtom = atom<TaskFlashState | null>(null);

/** Presses so far, module-wide for the same reason the flag is. */
let presses = 0;

/** Backstop for a flag whose row never mounts. Generous on purpose. */
const FLASH_SAFETY_MS = 12000;

/**
 * Give the flag up, but only if `taskId` still holds it.
 *
 * A row cut short by a newer chip still runs its own clear, and its backstop
 * still fires, both after the new row has claimed the flag. Unconditional,
 * either would take the new row's glow down with it.
 */
const release = (prev: TaskFlashState | null, taskId: string): TaskFlashState | null =>
  prev?.taskId === taskId ? null : prev;

/**
 * Give the flag up, but only if `press` is still the one that set it: the
 * backstop's own release, which must not put out a later press's glow of the
 * same row.
 */
const releasePress = (prev: TaskFlashState | null, press: number): TaskFlashState | null =>
  prev?.press === press ? null : prev;

/**
 * The backstop, run by the list that would show the row: while a flag is up
 * and the list is in front, a row that has not cleared it within the window
 * is one that never mounted — a filter that excludes it, a fetch that failed —
 * and the flag goes, so it cannot fire on some unrelated later visit.
 *
 * Here rather than at the chip, because the chip opens the task itself now
 * and the list may be minutes away: counted from the press, the window ran
 * out behind the detail screen and the reader came back to a quiet list.
 * `armed` is whether the list is in front; web passes true, since its list
 * is mounted only while it is, and native passes the screen's focus.
 */
export const useFlashBackstop = (armed: boolean): void => {
  const flagged = useAtomValue(flashTaskIdAtom);
  const setFlashId = useSetAtom(flashTaskIdAtom);
  const press = armed ? (flagged?.press ?? null) : null;
  useEffect(() => {
    if (press === null) return;
    const timer = setTimeout(() => setFlashId((prev) => releasePress(prev, press)), FLASH_SAFETY_MS);
    return () => clearTimeout(timer);
  }, [press, setFlashId]);
};

export type TaskFlash = {
  /** True while this row should run its one-shot glow. */
  flash: boolean;
  /** Call when the glow ends, so it cannot linger or re-fire. */
  clearFlash: () => void;
};

/**
 * The task a chip has just asked for, for a surface that has to bring it into
 * view rather than draw it — the list owns the scroll, not the row.
 *
 * Keyed on this rather than on the route, because the route cannot tell one
 * press from the next: a chip pressed twice names the same task both times, so
 * a scroll keyed on the route runs once and the second press leaves the row
 * wherever the user had scrolled to. `press` is what distinguishes them, so
 * read it rather than the id where "again" has to mean something.
 */
export const useFlashedTask = (): TaskFlashState | null => useAtomValue(flashTaskIdAtom);

/**
 * A row's own view of the flag: whether it is the row to glow, and how to put
 * the glow out when it has run.
 *
 * Subscribed through `selectAtom` so a row re-renders only when its own answer
 * changes. Reading the id and comparing in the body would subscribe every
 * mounted row to every other row's turn, and `memo` cannot help with that —
 * the subscription is inside the component.
 *
 * `clearFlash` gives the flag up only if this row still holds it, so a row
 * superseded by a newer chip cannot put out the new row's glow on its way out.
 */
export const useTaskFlash = (taskId: string): TaskFlash => {
  const flash = useAtomValue(
    useMemo(() => selectAtom(flashTaskIdAtom, (state) => state?.taskId === taskId), [taskId]),
  );
  const setFlashId = useSetAtom(flashTaskIdAtom);
  const clearFlash = useCallback(
    () => setFlashId((prev) => release(prev, taskId)),
    [setFlashId, taskId],
  );
  return { flash, clearFlash };
};

/**
 * Flags `taskId` as the row to glow wherever it next appears, taking the flag
 * from whichever row held it. The row clears it itself; the list it appears in
 * runs the backstop (`useFlashBackstop`) for a row that never does.
 */
export const useTriggerTaskFlash = (): ((taskId: string) => void) => {
  const setFlashId = useSetAtom(flashTaskIdAtom);
  return useCallback(
    (taskId: string) => {
      presses += 1;
      setFlashId({ taskId, press: presses });
    },
    [setFlashId],
  );
};
